import api from './api';

export interface PaymentApp {
  id: string;
  appCode: string;
  appName: string;
  isEnabled: number;
  channel: string;
  alipayAppPay: number;
  alipayPcPay: number;
  alipayWapPay: number;
  alipayQrPay: number;
  alipayBarPay: number;
  alipayMiniPay: number;
  wechatJsapiPay: number;
  wechatAppPay: number;
  wechatNativePay: number;
  wechatWapPay: number;
  wechatBarPay: number;
  walletPay: number;
  mockPay: number;
  alipayAppId?: string;
  alipayPrivateKey?: string;
  alipayPublicKey?: string;
  alipayNotifyUrl?: string;
  wechatAppId?: string;
  wechatMchId?: string;
  wechatSerialNo?: string;
  wechatApiKey?: string;
  wechatApiV3Key?: string;
  wechatPrivateKey?: string;
  wechatPayPublicKeyId?: string;
  wechatPayPublicKey?: string;
  wechatCertPath?: string;
  wechatNotifyUrl?: string;
  wechatRefundNotifyUrl?: string;
  refundAuditMode?: 'auto_review' | 'manual_review';
  aftersaleAuditMode?: 'auto_review' | 'manual_review';
  nearAftersaleMinutes?: number;
  payTimeoutMinutes?: number;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentAppListParams {
  page?: number;
  pageSize?: number;
  appName?: string;
  isEnabled?: number;
}

export const paymentService = {
  getConfig: async () => {
    const response = await api.get('/payment-apps/config');
    return response.data;
  },

  saveConfig: async (data: Partial<PaymentApp>) => {
    const response = await api.post('/payment-apps/config', data);
    return response.data;
  },

  list: async (params?: PaymentAppListParams) => {
    const response = await api.get('/payment-apps', { params });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/payment-apps/${id}`);
    return response.data;
  },

  create: async (data: Partial<PaymentApp>) => {
    const response = await api.post('/payment-apps', data);
    return response.data;
  },

  update: async (id: string, data: Partial<PaymentApp>) => {
    const response = await api.put(`/payment-apps/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/payment-apps/${id}`);
    return response.data;
  },

  toggleEnabled: async (id: string) => {
    const response = await api.post(`/payment-apps/${id}/toggle`);
    return response.data;
  }
};

export default paymentService;

export interface PaymentOrder {
  id: string;
  orderNo: string;
  bizOrderNo?: string;
  bizType?: string;
  appId?: string;
  appCode?: string;
  channel: string;
  payType?: string;
  payScene?: string;
  subject?: string;
  body?: string;
  amount: number;
  currency?: string;
  status: string;
  tradeNo?: string;
  userId?: string;
  userName?: string;
  payerOpenId?: string;
  notifyUrl?: string;
  returnUrl?: string;
  notifyStatus?: string;
  notifyCount?: number;
  notifyTime?: string;
  expireTime?: string;
  paidAt?: string;
  closedAt?: string;
  couponInfo?: PaymentOrderCouponInfo[];
  extraData?: string;
  remark?: string;
  createdAt: string;
}

export interface PaymentOrderCouponInfo {
  couponCode: string;
  verified: boolean;
  verifyStatus: number;
  amount: number;
}

export interface PaymentOrderStats {
  totalCount: number;
  totalAmount: number;
  paidCount: number;
  paidAmount: number;
  pendingCount: number;
  notifyFailedCount: number;
}

export interface PaymentOrderListResponse {
  list: PaymentOrder[];
  total: number;
  page: number;
  pageSize: number;
  stats?: PaymentOrderStats;
}

export interface RefundOrder {
  id: string;
  refundNo: string;
  orderId: string;
  orderNo: string;
  bizType?: string;
  applyType?: string;
  appId?: string;
  appCode?: string;
  channel: string;
  auditMode?: string;
  orderStatus?: string;
  refundAmount: number;
  reason?: string;
  description?: string;
  images?: string[];
  status: string;
  tradeNo?: string;
  userId?: string;
  userName?: string;
  operatorId?: string;
  operatorName?: string;
  reviewedAt?: string;
  refundedAt?: string;
  errorMsg?: string;
  extraData?: string;
  remark?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface TransferOrder {
  id: string;
  transferNo: string;
  appId?: string;
  appCode?: string;
  channel: string;
  transferType?: string;
  amount: number;
  payeeAccount?: string;
  payeeName?: string;
  payeeType?: string;
  title?: string;
  status: string;
  tradeNo?: string;
  operatorName?: string;
  transferredAt?: string;
  errorMsg?: string;
  createdAt: string;
}

export const paymentOrderService = {
  list: async (params?: any): Promise<PaymentOrderListResponse> => {
    const response = await api.get('/payment-orders', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/payment-orders/${id}`);
    return response.data;
  }
};

export const refundOrderService = {
  list: async (params?: any) => {
    const response = await api.get('/refund-orders', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/refund-orders/${id}`);
    return response.data;
  },
  review: async (id: string, data: { action: 'approve' | 'reject'; remark?: string; refundAmount?: number }) => {
    const response = await api.post(`/refund-orders/${id}/review`, data);
    return response.data;
  }
};

export const transferOrderService = {
  list: async (params?: any) => {
    const response = await api.get('/transfer-orders', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/transfer-orders/${id}`);
    return response.data;
  }
};

export interface PaymentNotify {
  id: string;
  notifyNo: string;
  orderId?: string;
  orderNo?: string;
  appId?: string;
  appCode?: string;
  channel: string;
  notifyType: string;
  notifyUrl?: string;
  requestData?: string;
  responseData?: string;
  status: string;
  retryCount: number;
  maxRetry: number;
  nextRetryAt?: string;
  successAt?: string;
  errorMsg?: string;
  createdAt: string;
}

export const paymentNotifyService = {
  list: async (params?: any) => {
    const response = await api.get('/payment-notifies', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/payment-notifies/${id}`);
    return response.data;
  },
  retry: async (id: string) => {
    const response = await api.post(`/payment-notifies/${id}/retry`);
    return response.data;
  }
};
